import { MutableRefObject, useEffect, useRef, useState } from "react";
import { extend, Extent, buffer, createEmpty, getCenter } from "ol/extent";
import { Vector as VectorSource, Cluster as ClusterSource, TileWMS, XYZ } from "ol/source";
import TileGrid from "ol/tilegrid/TileGrid";
import { Tile as TileLayer, Vector as VectorLayer } from "ol/layer";
import { Feature, View, Map } from "ol";
import { MultiPolygon, Point, Polygon, SimpleGeometry } from "ol/geom";
import { defaults as defaultControls } from "ol/control";
import { getClusterSymbol, getPolygonSymbol, getSimpleSymbol } from "./symbols";
import { IZemesnomaMapProps } from "../Providers/mapProvider";
import { BasemapButton } from "./Buttons/basemapButton";
import { LocationButton } from "./Buttons/GeoLocation";
import { HomeButton } from "./Buttons/Home";
import { AttributionButton } from "./Buttons/Attribution";
import { config } from "./config";
import { useNomaContext } from "../Contexts/MapContext";
import { Basemaps } from "./enums";
import { LayersButton } from "./Buttons/Layers";
import { ServerType } from "ol/source/wms";
import { DownloadButton } from "./Buttons/Download";

const clusterLayerId = config.default.clusterLayerId;
const polygonLayerId = config.default.polygonLayerId;
const getBuffer = (feature: Feature) => buffer(feature.getGeometry().getExtent(), 200);

let _map: MutableRefObject<Map>;
let sourceRef: MutableRefObject<VectorSource>;
let polygonSourceRef: MutableRefObject<VectorSource>;

export const useMap = (target: MutableRefObject<HTMLDivElement>, props: IZemesnomaMapProps): Map => {
    _map = useRef<Map>();
    sourceRef = useRef<VectorSource>(new VectorSource());
    polygonSourceRef = useRef<VectorSource>(new VectorSource());

    const {
        setBasemap,
        locateFeature,
        zoomToSourceFeatures,
        setZoomToSourceFeatures,
        setIdentifiedFeature,
        toggleLayersMenuVisible
    } = useNomaContext();

    useEffect(() => {
        if (target.current) {
            const tileGrid = new TileGrid(config.basemapLVM.tileGrid);
            const mammaLayer = new TileLayer({
                source: new XYZ({
                    url: config.basemapLVM.source.url,
                    maxZoom: config.basemapLVM.source.maxZoom,
                    projection: config.basemapLVM.source.projection,
                    tileGrid,
                    crossOrigin: config.basemapLVM.source.crossOrigin
                }),
                visible: true,
                properties: { id: Basemaps.Mamma }
            });
            const ortoLayer = new TileLayer({
                source: new TileWMS({
                    url: config.basemapOrto.source.url,
                    params: config.basemapOrto.source.params,
                    projection: config.basemapOrto.source.projection,
                    serverType: config.basemapOrto.source.serverType as ServerType,
                    crossOrigin: config.basemapOrto.source.crossOrigin
                }),
                visible: false,
                properties: { id: Basemaps.Orto }
            });
            const polygonLayer = new VectorLayer({
                properties: { id: polygonLayerId },
                source: polygonSourceRef.current,
                minZoom: config.default.polygonZoomThreshold,
                style: (f: Feature) => getPolygonSymbol(f)
            });
            const clusterSource = new ClusterSource({
                distance: config.default.clusterDistance,
                source: sourceRef.current,
                geometryFunction: (feature: Feature) => {
                    const geometry = feature.getGeometry();
                    const type = geometry.getType();
                    return type === "Polygon"
                        ? (geometry as Polygon).getInteriorPoint()
                        : type === "MultiPolygon"
                        ? (geometry as MultiPolygon).getInteriorPoints().getPoint(1)
                        : (geometry as Point);
                }
            });
            const clusterLayer = new VectorLayer({
                properties: { id: clusterLayerId },
                source: clusterSource,
                maxZoom: config.default.polygonZoomThreshold,
                style: (clusterFeature) => {
                    const clusterFeatures = clusterFeature.get("features");
                    return clusterFeatures.length > 1
                        ? getClusterSymbol(clusterFeatures.length)
                        : getSimpleSymbol(clusterFeatures[0].get("VISIBLE"));
                }
            });

            _map.current = new Map({
                layers: [mammaLayer, ortoLayer, polygonLayer, clusterLayer],
                target: target.current,
                view: new View({
                    projection: config.default.projection,
                    center: config.default.center,
                    zoom: config.default.zoom,
                    enableRotation: false,
                    minZoom: config.default.minZoom
                }),
                controls: defaultControls({ zoom: false, rotate: false, attribution: false }).extend([
                    new BasemapButton(toggleBasemap),
                    new LocationButton(),
                    new HomeButton(),
                    new LayersButton(toggleLayersMenuVisible),
                    new DownloadButton(),
                    new AttributionButton()
                ])
            });

            _map.current.on("moveend", () => document.dispatchEvent(new Event("moveend")));

            _map.current.on("pointermove", (evt) => {
                if (evt.dragging) {
                    return;
                }

                const hasFeatureAtPixel = _map.current.hasFeatureAtPixel(evt.pixel);

                target.current.style.cursor = hasFeatureAtPixel ? "pointer" : null;
            });

            _map.current.on("click", (evt) => {
                if (evt.dragging) {
                    return;
                }
                const hasFeatureAtPixel = _map.current.hasFeatureAtPixel(evt.pixel);
                if (!hasFeatureAtPixel) {
                    return;
                }

                _map.current.forEachFeatureAtPixel(evt.pixel, handlePointClick, {
                    layerFilter: (layer) => layer.get("id") === clusterLayerId
                });
                _map.current.forEachFeatureAtPixel(evt.pixel, handlePolygonClick, {
                    layerFilter: (layer) => layer.get("id") === polygonLayerId
                });
            });

            //@ts-ignore
            _map.current.once("postcompose", zoomHome);

            const handlePointClick = (feature: Feature) => {
                const clusteredFeatures: Feature[] = feature.get("features");
                if (clusteredFeatures.length) {
                    if (clusteredFeatures.length === 1) {
                        setIdentifiedFeature(clusteredFeatures[0]);
                    } else {
                        const extent = clusteredFeatures.reduce<Extent>((result, feature) => {
                            return result === null ? getBuffer(feature) : extend(result, getBuffer(feature));
                        }, null);
                        const view = _map.current.getView();
                        view.fit(extent, { padding: [60, 60, 60, 60], duration: 500 });
                    }
                }
            };
            const handlePolygonClick = (feature: Feature) => {
                setIdentifiedFeature(feature);
            };
        }
    }, []);

    const toggleBasemap = (mapId: Basemaps) => {
        _map.current
            .getLayers()
            .getArray()
            .map((layer) => {
                const id = layer.get("id");
                id === Basemaps.Orto
                    ? layer.set("visible", mapId === Basemaps.Mamma)
                    : id === Basemaps.Orto
                    ? layer.set("visible", mapId === Basemaps.Orto)
                    : null;
            });
        setBasemap(mapId === Basemaps.Orto ? Basemaps.Mamma : Basemaps.Orto);
    };

    useEffect(() => {
        locateFeature && zoomToFeature(locateFeature.feature);
    }, [locateFeature]);

    useEffect(() => {
        if (props.features && props.features.length) {
            sourceRef.current.clear();
            sourceRef.current.addFeatures(props.features);
            polygonSourceRef.current.clear();
            polygonSourceRef.current.addFeatures(props.features);
        } else {
            sourceRef.current.clear();
            polygonSourceRef.current.clear();
        }
    }, [props.forceUpdate, props.features]);

    useEffect(() => {
        _map.current.once("postcompose", () => {
            zoomToSourceFeatures === 1 && zoomHome(() => setZoomToSourceFeatures(null));
            zoomToSourceFeatures > 1 &&
                zoomToExtent(sourceRef.current.getExtent(), () => setZoomToSourceFeatures(null));
        });
    }, [zoomToSourceFeatures]);
    //@ts-ignore
    window.map = _map.current;
    return _map.current;
};

const zoomHome = (callback?: () => void) => {
    const view = _map.current.getView();
    //@ts-ignore
    const resolutionX = config.default.extentSize[0] / view.viewportSize_[0];
    //@ts-ignore
    const resolutionY = config.default.extentSize[1] / view.viewportSize_[1];
    const zoom = view.getZoomForResolution(resolutionX > resolutionY ? resolutionX : resolutionY);

    view.fit(new Point(config.default.center), {
        maxZoom: zoom,
        duration: 1000,
        callback
    });
};

const zoomToExtent = (extent: Extent, callback?: () => void) => {
    if (!extent || extent.includes(Infinity)) {
        return;
    }
    document.dispatchEvent(new Event("stopTracking"));
    const view = _map.current.getView();
    view.fit(extent, { duration: 500, padding: [60, 60, 60, 60], callback });
};

const zoomToFeature = (feature: Feature): void => {
    const view = _map.current.getView();
    const geometry = feature.getGeometry() as SimpleGeometry;
    const currentZoom = view.getZoom();
    const maxZoom = currentZoom > config.default.zoomToLevel ? currentZoom : config.default.zoomToLevel;
    document.dispatchEvent(new Event("stopTracking"));
    view.fit(geometry, { duration: 500, maxZoom });
};
